// @ts-check
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { formatDay, label, WHOLESALE_PLACEMENT_KIND } from '../../utils/wholesale';
import { card, EnumSelect, Field, input, primaryBtn } from './shared';

/** One line per piece: "style, size, qty, MRP". */
const parseItems = (text) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [style, size, quantity, mrp] = line.split(',').map((s) => s.trim());
      return { style, size: size || null, quantity: Number(quantity) || 1, mrp: mrp ? Number(mrp) : null };
    });

/** What went out to a door under an active agreement. Shown only once an agreement is active. */
const PlacementsTab = ({ partner }) => {
  const toast = useToast();
  const active = partner.agreements.find((a) => a.status === 'active');
  const [placements, setPlacements] = useState([]);
  const [form, setForm] = useState({ kind: 'samples', challan_number: '', dispatched_at: '', items: '' });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const load = useCallback(async () => {
    try {
      setPlacements(await adminWholesaleService.listPlacements(partner.id));
    } catch (err) {
      toast.error(err.message || 'Failed to load the placements');
    }
  }, [partner.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await adminWholesaleService.addPlacement(active.id, {
        kind: form.kind,
        challan_number: form.challan_number.trim() || null,
        dispatched_at: form.dispatched_at || null,
        items: parseItems(form.items),
      });
      toast.success('Placement logged');
      setForm({ kind: 'samples', challan_number: '', dispatched_at: '', items: '' });
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to log the placement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className={`${card} p-5 space-y-3`}>
        <h2 className="text-base font-semibold">Log a placement</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Kind">
            <EnumSelect value={form.kind} onChange={(kind) => set({ kind })} options={Object.values(WHOLESALE_PLACEMENT_KIND)} />
          </Field>
          <Field label="Challan number" hint={form.kind === 'consignment' ? 'Required for consignment' : undefined}>
            <input className={input} value={form.challan_number} onChange={(e) => set({ challan_number: e.target.value })} />
          </Field>
          <Field label="Dispatched on">
            <input type="date" className={input} value={form.dispatched_at} onChange={(e) => set({ dispatched_at: e.target.value })} />
          </Field>
        </div>
        <Field label="Pieces" hint="One per line: style, size, quantity, MRP">
          <textarea className={input} rows={4} placeholder="Meadow mini, S, 1, 9500" value={form.items} onChange={(e) => set({ items: e.target.value })} />
        </Field>
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving...' : 'Log placement'}
        </button>
      </div>
      {placements.length > 0 && (
        <ul className="space-y-3">
          {placements.map((p) => (
            <li key={p.id} className={`${card} p-4 text-sm`}>
              <p className="font-medium">
                {label(p.kind)}
                {p.challan_number ? ` · ${p.challan_number}` : ''} · {formatDay(p.dispatched_at)}
              </p>
              <p className="text-muted-foreground mt-1">
                {p.items.map((i) => `${i.style}${i.size ? ` ${i.size}` : ''} ×${i.quantity}`).join(', ') || 'No pieces listed'}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlacementsTab;
